import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator 
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MoreHorizontal, Trash2, Edit, Send } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useNotify } from '@/hooks/useNotify';
import SendEstimateDialog from '../SendEstimateDialog';

interface EstimateActionsProps {
  estimateId: string;
  estimateTitle: string;
  estimateStatus: string;
  estimateNumber?: string;
  customerId?: string;
  companyId?: string;
  onDelete?: () => void;
}

const EstimateActions: React.FC<EstimateActionsProps> = ({
  estimateId,
  estimateTitle,
  estimateStatus,
  estimateNumber,
  customerId,
  companyId,
  onDelete
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const queryClient = useQueryClient();
  const notify = useNotify();

  const deleteEstimateMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('delete_estimate_cascade', {
        p_estimate_id: estimateId
      });
      
      if (error) throw error;
      
      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete estimate');
      }
      
      return result;
    },
    onSuccess: () => {
      notify.success('Estimate deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      onDelete?.();
    },
    onError: (error) => {
      console.error('Error deleting estimate:', error);
      notify.error(error);
    },
  });

  const handleDelete = () => {
    deleteEstimateMutation.mutate();
    setShowDeleteDialog(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to={`/estimates/edit/${estimateId}`} className="flex items-center">
              <Edit className="h-4 w-4 mr-2" />
              Edit Estimate
            </Link>
          </DropdownMenuItem>
          
          {companyId && (
            <DropdownMenuItem onClick={() => setShowSendDialog(true)}>
              <Send className="h-4 w-4 mr-2" />
              Send to Customer
            </DropdownMenuItem>
          )}
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem
            onClick={() => setShowDeleteDialog(true)}
            className="text-destructive focus:text-destructive"
            disabled={deleteEstimateMutation.isPending}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Estimate
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Estimate</AlertDialogTitle>
            <AlertDialogDescription>
              {estimateStatus === 'approved' ? (
                <div className="space-y-2">
                  <p>Are you sure you want to delete the estimate "{estimateTitle}"?</p>
                  <p className="text-orange-600 font-medium">
                    ⚠️ This estimate is approved and has related records that will also be permanently deleted:
                  </p>
                  <ul className="list-disc list-inside text-sm text-muted-foreground ml-4">
                    <li>Associated project data</li>
                    <li>Estimate versions and change orders</li>
                    <li>Related invoices (if any)</li>
                    <li>All line items and calculations</li>
                  </ul>
                  <p className="text-red-600 font-medium">This action cannot be undone.</p>
                </div>
              ) : (
                `Are you sure you want to delete the estimate "${estimateTitle}"? This action cannot be undone.`
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteEstimateMutation.isPending}
            >
              {deleteEstimateMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {companyId && (
        <SendEstimateDialog
          open={showSendDialog}
          onOpenChange={setShowSendDialog}
          estimateId={estimateId}
          estimateNumber={estimateNumber}
          customerId={customerId}
          companyId={companyId}
        />
      )}
    </>
  );
};

export default EstimateActions;